/* MANGO MOJITO · The Archive on the live Craft theme (ported from the draft theme's MmArchive, 2026-09-29).
   Search, then Type, Price, Size and Sort, one choice each, over every pair at once. The first 250 pairs arrive with
   the page; the rest are fetched page by page and added to the grid, then the page numbers are hidden. Each option
   shows how many pairs it holds. One chip is open at a time; Escape or a click outside closes it. */
class MmArchiveLive extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.mma__body');
    this.grid = this.querySelector('.mma__grid');
    this.status = this.querySelector('[data-mma-status]');
    if (!this.body || !this.grid) return;
    this.state = { type: '', price: '', size: '', sort: 'style', q: '' };
    this.labels = { one: this.body.dataset.countOne || '', other: this.body.dataset.countOther || '', sizePrefix: this.body.dataset.sizePrefix || '' };

    this.addEventListener('click', (e) => {
      const summary = e.target.closest('.mma-menu > summary');
      if (summary) this.closeChips(summary.parentElement);
      const opt = e.target.closest('.mma-opt');
      if (opt) { this.choose(opt); return; }
      if (e.target.closest('[data-mma-clear]')) this.clear();
    });
    this.onDocClick = (e) => { if (!e.target.closest('.mma-menu')) this.closeChips(); };
    this.onKey = (e) => {
      if (e.key !== 'Escape') return;
      const open = this.querySelector('.mma-menu[open]');
      if (open) { open.open = false; open.querySelector('summary').focus(); }
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);

    const search = this.querySelector('[data-mma-search]');
    if (search) {
      let timer;
      search.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => { this.state.q = search.value.trim().toLowerCase(); this.render(); }, 150);
      });
    }
    this.loadRest().then(() => { this.buildOptions(); this.render(); });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
  }

  closeChips(except) {
    this.querySelectorAll('.mma-menu[open]').forEach((d) => { if (d !== except) d.open = false; });
  }

  items() { return Array.from(this.grid.querySelectorAll('[data-mma-item]')); }

  async loadRest() {
    const pages = Number(this.body.dataset.pages || 1);
    for (let page = 2; page <= pages; page++) {
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('page', page);
        const html = await fetch(url).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); });
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-mma-item]').forEach((li) => this.grid.append(document.adoptNode(li)));
      } catch (err) {
        return; // keep the page numbers so the remaining pairs stay reachable
      }
    }
    const nav = this.querySelector('[data-mma-pages]');
    if (nav) nav.hidden = true;
  }

  buildOptions() {
    const items = this.items();
    const tally = (key) => items.reduce((m, li) => { const v = li.dataset[key]; if (v) m.set(v, (m.get(v) || 0) + 1); return m; }, new Map());
    this.querySelectorAll('[data-mma-total]').forEach((el) => { el.textContent = items.length; });
    const types = Array.from(tally('type')).sort((a, b) => a[0].localeCompare(b[0]));
    this.fill('type', types.map(([v, n]) => ({ value: v, label: v, count: n })));
    const prices = Array.from(tally('price')).sort((a, b) => Number(a[0]) - Number(b[0]));
    this.fill('price', prices.map(([v, n]) => ({ value: v, label: this.money(v), count: n })));
    const sizes = Array.from(tally('size')).sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]));
    this.fill('size', sizes.map(([v]) => ({ value: v, label: v })));
  }

  fill(key, options) {
    const box = this.querySelector(`[data-mma-opts="${key}"]`);
    if (!box) return;
    options.forEach((o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'mma-opt';
      b.dataset.value = o.value;
      b.setAttribute('aria-pressed', 'false');
      const label = document.createElement('span');
      label.textContent = o.label;
      b.append(label);
      if (o.count !== undefined) {
        const c = document.createElement('span');
        c.className = 'mma-opt__count';
        c.textContent = o.count;
        b.append(c);
      }
      if (key === 'size') b.setAttribute('aria-label', this.labels.sizePrefix + o.label);
      box.append(b);
    });
  }

  choose(opt) {
    const box = opt.closest('[data-mma-opts]');
    this.state[box.dataset.mmaOpts] = opt.dataset.value;
    const chip = opt.closest('.mma-menu');
    chip.open = false;
    chip.querySelector('summary').focus();
    this.render();
  }

  clear() {
    Object.assign(this.state, { type: '', price: '', size: '', q: '' });
    const search = this.querySelector('[data-mma-search]');
    if (search) search.value = '';
    this.render();
  }

  money(cents) {
    return `฿${(Number(cents) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }

  render() {
    const { type, price, size, sort, q } = this.state;
    const items = this.items();
    let shown = 0;
    items.forEach((li) => {
      const d = li.dataset;
      const ok = (!type || d.type === type) && (!price || d.price === price) && (!size || d.size === size)
        && (!q || d.name.includes(q) || d.no.includes(q) || d.type.toLowerCase().includes(q));
      li.hidden = !ok;
      if (ok) shown++;
    });

    const num = (v) => parseFloat(v) || 0;
    const byName = (a, b) => a.dataset.name.localeCompare(b.dataset.name);
    const orders = {
      style: byName,
      'price-asc': (a, b) => num(a.dataset.price) - num(b.dataset.price) || byName(a, b),
      'price-desc': (a, b) => num(b.dataset.price) - num(a.dataset.price) || byName(a, b),
      size: (a, b) => num(a.dataset.size) - num(b.dataset.size) || byName(a, b),
      number: (a, b) => num(a.dataset.no) - num(b.dataset.no),
    };
    items.sort(orders[sort] || byName).forEach((li) => this.grid.append(li));

    this.querySelectorAll('[data-mma-opts]').forEach((box) => {
      const key = box.dataset.mmaOpts;
      const value = this.state[key];
      let label = null;
      box.querySelectorAll('.mma-opt').forEach((b) => {
        const on = b.dataset.value === value;
        b.setAttribute('aria-pressed', String(on));
        if (on) label = b.querySelector('span') ? b.querySelector('span').textContent : b.textContent;
      });
      const chip = box.closest('.mma-menu');
      const summary = chip.querySelector('.mma-chip');
      const valueEl = chip.querySelector('[data-mma-value]');
      if (!chip.dataset.allLabel) chip.dataset.allLabel = valueEl.textContent;
      if (key === 'sort') valueEl.textContent = label || chip.dataset.allLabel;
      else valueEl.textContent = value ? (key === 'size' ? this.labels.sizePrefix + value : label) : chip.dataset.allLabel;
      summary.classList.toggle('is-active', key !== 'sort' && !!value);
    });

    const narrowed = !!(type || price || size || q);
    this.querySelectorAll('[data-mma-clear]').forEach((b) => { if (!b.closest('[data-mma-empty]')) b.hidden = !narrowed; });
    const empty = this.querySelector('[data-mma-empty]');
    if (empty) empty.hidden = shown > 0;

    const text = (shown === 1 ? this.labels.one : this.labels.other).replace(/\d+/, shown);
    this.querySelectorAll('[data-mma-count]').forEach((el) => { el.textContent = text; });
    if (this.status && this.rendered) this.status.textContent = text;
    this.rendered = true;
  }
}

if (!customElements.get('mm-archive-live')) customElements.define('mm-archive-live', MmArchiveLive);
